import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { TRANSLATION_KEYS } from '@core/internationalization/translations_keys';
import { ProductService } from '@core/product/product.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-page-not-found',
    standalone: true,
    imports: [TranslatePipe],
    template: `
        <div
            class="relative flex h-full w-full flex-1 items-center justify-center overflow-hidden bg-twMainBackground px-6 py-10">
            <!-- Atmospheric backdrop -->
            <div class="pointer-events-none absolute inset-0 -z-10">
                <div
                    class="absolute left-1/2 top-1/2 size-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-60 blur-3xl"
                    style="background: conic-gradient(from 180deg at 50% 50%, rgba(49,87,218,0.10) 0deg, rgba(168,85,247,0.10) 120deg, rgba(49,87,218,0.10) 240deg, rgba(49,87,218,0.10) 360deg);"></div>
                <div
                    class="absolute left-1/2 top-[42%] size-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/70 blur-2xl"></div>
                <div
                    class="absolute inset-0 opacity-50"
                    style="background-image: linear-gradient(to right, rgba(15,26,65,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,26,65,0.04) 1px, transparent 1px); background-size: 48px 48px; mask-image: radial-gradient(ellipse at center, black 25%, transparent 70%); -webkit-mask-image: radial-gradient(ellipse at center, black 25%, transparent 70%);"></div>
            </div>

            <div class="relative flex w-full max-w-[480px] flex-col items-center gap-6 text-center">
                <!-- Big 404 mark -->
                <div
                    class="relative flex flex-col items-center opacity-0 animate-rise-fade [animation-delay:80ms]">
                    <span
                        class="font-mono text-[140px] font-semibold leading-none tracking-[-0.05em] text-transparent"
                        style="background: linear-gradient(180deg, rgb(49,87,218) 0%, rgba(49,87,218,0.25) 100%); -webkit-background-clip: text; background-clip: text;">
                        404
                    </span>
                    <span
                        class="absolute inset-x-0 bottom-2 mx-auto h-px w-24 bg-gradient-to-r from-transparent via-primary-400/60 to-transparent"></span>
                </div>

                <!-- Eyebrow + title + subtitle -->
                <div
                    class="flex flex-col items-center gap-3 opacity-0 animate-rise-fade [animation-delay:200ms]">
                    <span class="tw-section-label">
                        {{ KEYS.eyebrow | translate }}
                    </span>
                    <h1 class="tw-page-heading">{{ KEYS.title | translate }}</h1>
                    <p class="max-w-[420px] text-balance text-[13.5px] leading-relaxed text-zinc-500">
                        {{ KEYS.subtitle | translate }}
                    </p>
                </div>

                <!-- Actions -->
                <div
                    class="flex items-center gap-2 opacity-0 animate-rise-fade [animation-delay:320ms]">
                    <button class="tw-btn-ghost" type="button" (click)="handleBack()">
                        {{ KEYS.goBack | translate }}
                    </button>
                    <button class="tw-btn-primary" type="button" (click)="handleHome()">
                        {{ KEYS.goHome | translate }}
                    </button>
                </div>
            </div>
        </div>
    `,
    styles: [':host { display: flex; flex: 1 1 0%; width: 100%; height: 100%; }'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageNotFoundComponent {
    private readonly router = inject(Router);
    private readonly location = inject(Location);
    private readonly productS = inject(ProductService);

    protected readonly KEYS = TRANSLATION_KEYS.common.notFound;

    handleHome() {
        this.router.navigateByUrl(this.productS.homeUrl());
    }

    handleBack() {
        this.location.back();
    }
}
